/**
 * agentComposition — composite-agent grouping for the Agents list.
 *
 * A "wrapper" agent's graph dispatches OTHER deployed agents as sub-workflows;
 * the backend list endpoint surfaces those child marketplace slugs on each row
 * as `composedOf: [slug, ...]` (stamped at deploy from the graph's static
 * `dispatchesWorkflow` node markers; see backend workflow-composition.js).
 *
 * groupAgents() applies the LIST-presentation rules:
 *   - A row whose slug (workflowType OR marketplaceSlug) appears in ANY OTHER
 *     row's member set is WRAPPED → hidden from the top level. This applies
 *     recursively for free: a hidden wrapper's own composedOf still hides ITS
 *     members (membership is computed over ALL rows, not just visible ones).
 *   - The wrapper row gets `members: [row, ...]` — the DEPLOYED member rows
 *     resolved in dispatch order, so the UI can render their icons as a strip.
 *   - Dangling reference (member slug with no deployed row) → resolved into
 *     `membersMissing: [slug, ...]` so the UI can SAY a declared child is not
 *     deployed (that dispatch 404s at run time — "Child workflow not found"),
 *     instead of silently under-counting. Never crashes. Two wrappers sharing
 *     a member → member hidden once, resolved into BOTH strips.
 *     Self-reference → ignored (a row never hides itself).
 *
 * Pure + non-mutating: input rows are never modified; wrapper rows are shallow
 * -copied to attach `members`. Detail pages / deep links are unaffected — this
 * only shapes the LIST.
 */

export const rowMatchesSlug = (row, slug) =>
  !!row && (row.workflowType === slug || row.marketplaceSlug === slug);

/**
 * deleteCascadeDrop — which cached rows leave when ONE agent is deleted.
 *
 * The server's delete CASCADES into the agent's platform-owned member rows
 * (backend workflows.deleteSubgraphChildren), so an optimistic cache drop must
 * mirror it or the members ORPHAN client-side: their wrapper gone, groupAgents
 * un-hides them and they surface as top-level agents until the next natural
 * refetch (which useDeleteAgent deliberately defers) — the deleted-children
 * flash. Mirrored rules, from the list row's own fields:
 *   - the deleted row's `composedOf` names its members;
 *   - `platformOwned` is THE cascade predicate, shipped by the backend
 *     (workflow-slugs.isPlatformOwnedChild — one decider, never re-derived
 *     here from hidden/systemManaged guesswork);
 *   - a member ANOTHER surviving agent still declares is KEPT, exactly like
 *     the server keeps it.
 *
 * @param {Array} rows     the cached list rows (pre-delete)
 * @param {(row)=>boolean} matches  identifies the deleted agent's own row
 * @returns {(row)=>boolean} true = drop this row from the cache
 */
export function deleteCascadeDrop(rows, matches) {
  const list = Array.isArray(rows) ? rows : [];
  const deleted = list.find((a) => matches(a));
  const memberSlugs = (Array.isArray(deleted?.composedOf) ? deleted.composedOf : [])
    .filter((s) => typeof s === 'string' && s);
  const stillDeclared = new Set();
  list.forEach((a) => {
    if (matches(a)) return;
    (Array.isArray(a?.composedOf) ? a.composedOf : []).forEach((s) => stillDeclared.add(s));
  });
  const cascaded = (a) =>
    a?.platformOwned === true
    && memberSlugs.some((s) => rowMatchesSlug(a, s) && !stillDeclared.has(s));
  return (a) => matches(a) || cascaded(a);
}

export function groupAgents(rows) {
  const list = Array.isArray(rows) ? rows.filter((r) => r && typeof r === 'object') : [];

  // Effective member slugs per row = composedOf minus self-references.
  const memberSlugsOf = (row) => {
    const slugs = Array.isArray(row.composedOf) ? row.composedOf : [];
    return slugs.filter((s) => typeof s === 'string' && s && !rowMatchesSlug(row, s));
  };

  // Wrapped = referenced by ANY OTHER row (visible or itself hidden — that's
  // what makes nesting recursive).
  const wrapped = new Set();
  for (const row of list) {
    for (const slug of memberSlugsOf(row)) {
      for (const other of list) {
        if (other !== row && rowMatchesSlug(other, slug)) wrapped.add(other);
      }
    }
  }

  return list
    .filter((row) => !wrapped.has(row))
    .map((row) => {
      const membersMissing = [];
      const members = memberSlugsOf(row)
        .map((slug) => {
          const found = list.find((r) => r !== row && rowMatchesSlug(r, slug));
          if (!found) membersMissing.push(slug);
          return found;
        })
        .filter(Boolean);
      if (!members.length && !membersMissing.length) return row;
      return { ...row, members, ...(membersMissing.length ? { membersMissing } : {}) };
    });
}

export default groupAgents;
